import { Button, Container, TableItem, TableMessage } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { Table } from 'components/organisms';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useTranslation } from 'react-i18next';
import { PiPlus, PiTrash } from 'react-icons/pi';
import { useGetLanguages } from 'services/enums';
import { IVideoMutationForm } from 'services/video/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { getLanguageSelectOptions } from 'utils/global/language.utils';

const VideoSubtitles = () => {
  const {
    values,
    status,
    setFieldValue,
  }: IFormikContextType<IVideoMutationForm> =
    useFormikContext<IVideoMutationForm>();
  const { t } = useTranslation();

  const languages = useGetLanguages();

  const setNewTitle = () =>
    setFieldValue(`subtitles[${values.subtitles.length}]`, {
      id: values.subtitles.length,
      language: -1,
      title: '',
      version: '',
    });

  const removeTitle = (id: number) =>
    setFieldValue(
      'subtitles',
      values.subtitles
        .filter((_, key) => key !== id)
        .map((title, key) => ({ ...title, id: key }))
    );

  return (
    <div className='flex flex-col w-full gap-4'>
      <Container>
        <Container.Content>
          <div
            className={
              status?.displayMode ? 'w-full pointer-events-none' : 'w-full'
            }>
            <FormInput.Checkbox
              name='isInstrumental'
              label={t('input.label.instrumental')}
            />
          </div>
        </Container.Content>
      </Container>
      {!values.isInstrumental && (
        <Table
          id={'VideoSubtitles'}
          size='compact'
          data={{
            nodes: values.subtitles.map((title, key) => ({
              ...title,
              id: key,
            })),
          }}
          columns={[
            {
              label: t('input.label.subtitles'),
              renderCell: ({ subtitles, id }) => (
                <TableItem>
                  {status?.displayMode ? (
                    <div className='flex max-w-full whitespace-normal h-[100px] overflow-y-scroll'>
                      {subtitles}
                    </div>
                  ) : (
                    // <TableItem.Text text={subtitles} />
                    <FormInput.Field
                      name={`subtitles[${id}].subtitles`}
                      label={t('input.label.subtitles')}
                      style={{
                        height: 100,
                        resize: 'none',
                        width: '100%',
                        maxWidth: '100%',
                      }}
                    />
                  )}
                </TableItem>
              ),
              resize: true,
            },
            {
              label: t('input.label.language'),
              renderCell: ({ language, id }) => (
                <TableItem className='visible overflow-visible'>
                  {status?.displayMode ? (
                    <TableItem.Text text={t(`enum.LANGUAGE.${language}`)} />
                  ) : (
                    <FormInput.Select
                      name={`subtitles[${id}].language`}
                      label={t('input.label.language')}
                      value={{
                        value: values.subtitles[id].language,
                        label: t(`enum.LANGUAGE.${language}`),
                      }}
                      options={getLanguageSelectOptions(
                        languages.data,
                        values.subtitles.map(s => ({
                          title: s.subtitles,
                          version: '',
                          language: s.language,
                        }))
                      )}
                    />
                  )}
                </TableItem>
              ),
            },
            {
              label: '',
              renderCell: ({ id }) => (
                <TableItem justifyPosition='end'>
                  {status?.displayMode ? null : (
                    <Button
                      variant={BUTTON_VARIANT.remove}
                      onlyIcon
                      isClear
                      onClick={() => removeTitle(id)}>
                                            <Button.Description description={t('button.remove')} />

                                            <Button.Icon icon={<PiTrash />} />
                    </Button>
                  )}
                </TableItem>
              ),
            },
          ]}
          tableOptions={
            status?.displayMode
              ? undefined
              : {
                  renderAfterTable: () => (
                    <tfoot className='full-width-footer'>
                      <TableMessage
                        className='cursor-pointer'
                        onClick={setNewTitle}>
                        <TableItem.Text text={t('button.add')} />
                        <PiPlus />
                      </TableMessage>
                    </tfoot>
                  ),
                }
          }
        />
      )}
    </div>
  );
};

export default VideoSubtitles;
